import { useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { AuthContext } from "~/utils/useAuth";
import { styled, vh, darkTheme } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import {
  getPost,
  createComment,
  deleteComment,
  deletePost,
  likeComment,
  unlikeComment,
  reportContent,
  CommunityPostWithComments,
  CommunityComment
} from "~/api/communityApi";
import { allDecks } from "~/ui/menus/DeckMenu";
import { DeckCard } from "~/types/room/types";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { motion } from "framer-motion";
import { trackEvent } from "~/api/analyticsApi";
import { Button } from "~/ui/components/Button";
import { imageUrl } from "~/utils/imageurl";
import { sendToastMessageWithFallbackCode, clearToast } from "~/utils/handleToasts";
import { Message } from "~/ui/components/Message";
import { ProfilePopover } from "./ProfilePopover";

interface CommunityPostDetailProps {
  postId: string;
}

export const CommunityPostDetail = ({ postId }: CommunityPostDetailProps) => {
  const { user } = useContext(AuthContext);
  const { appDarkMode: dk } = useSnapshot(localState);
  const [, setLocation] = useLocation();
  const [post, setPost] = useState<CommunityPostWithComments | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [reportingId, setReportingId] = useState<string | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [showKeywords, setShowKeywords] = useState(false);

  const MAX_COMMENT_LENGTH = 1000;

  const getCardKeywords = (cardName: string, isReversed: boolean): string[] => {
    const baseName = cardName.replace(/ \(Reversed\)| Reversed/g, '').trim();
    const deckSlug = post?.deckName;
    const deck = deckSlug
      ? allDecks.find(d => d.basePath === `/decks/${deckSlug}`)
      : null;
    // Try the post's deck first, then fall back to rider-waite
    const decksToSearch = deck
      ? [deck, ...allDecks.filter(d => d.basePath === '/decks/rider-waite')]
      : allDecks.filter(d => d.basePath === '/decks/rider-waite');
    for (const d of decksToSearch) {
      const match = d.cards.find((c: DeckCard) => c.name === baseName);
      if (match) {
        if (isReversed && match.keywordsReverse?.length > 0 && match.keywordsReverse[0].length > 0) {
          return match.keywordsReverse;
        }
        return match.keywords || [];
      }
    }
    return [];
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);

  useEffect(() => {
    const fetchPost = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const data = await getPost(postId);
        setPost(data);
      } catch (error) {
        console.error('Failed to fetch post:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user && postId) {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('utm_source');
      trackEvent("/community/viewed_post", source ? { utm_source: source } : undefined);
      fetchPost();
    }
  }, [user?.uuid, postId]);

  const handleSubmitComment = async () => {
    if (!commentText.trim() || submitting) return;

    try {
      setSubmitting(true);
      setError(null);
      const newComment = await createComment(postId, commentText.trim());
      setPost(prev => prev ? {
        ...prev,
        comments: [...prev.comments, newComment]
      } : null);
      setCommentText("");
      trackEvent("/community/commented_on_post");
      // Scroll to the new comment after a brief delay for render
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add comment");
      clearToast(); // suppress the global axios error toast — we show inline error instead
      // Auto-clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setPost(prev => prev ? {
        ...prev,
        comments: prev.comments.filter(c => c.id !== commentId)
      } : null);
      trackEvent("/community/deleted_comment");
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleLikeComment = async (commentId: string, isLiked: boolean) => {
    // Optimistic update - update UI immediately
    setPost(prev => prev ? {
      ...prev,
      comments: prev.comments.map(c =>
        c.id === commentId
          ? {
            ...c,
            hasLiked: !isLiked,
            likeCount: c.likeCount + (isLiked ? -1 : 1)
          }
          : c
      )
    } : null);

    try {
      if (isLiked) {
        await unlikeComment(commentId);
      } else {
        await likeComment(commentId);
        trackEvent("/community/liked_comment");
      }
    } catch (err) {
      // Revert on error
      setPost(prev => prev ? {
        ...prev,
        comments: prev.comments.map(c =>
          c.id === commentId
            ? {
              ...c,
              hasLiked: isLiked,
              likeCount: c.likeCount + (isLiked ? 1 : -1)
            }
            : c
        )
      } : null);
      console.error('Failed to toggle like:', err);
    }
  };

  const handleDeletePost = async () => {
    try {
      await deletePost(postId);
      trackEvent("/community/deleted_post");
      setLocation("/community");
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks <= 4) return `${diffWeeks}w`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isMyPost = post?.authorId === user?.uuid;

  if (loading) {
    return (
      <StyledPostDetailPage>
        <div className="loading-container">
          <SimpleLoadingIndicator style={{ position: 'static', width: '24px', height: '24px', opacity: 0.5 }} />
        </div>
      </StyledPostDetailPage>
    );
  }

  if (!post) {
    return (
      <StyledPostDetailPage>
        <div className="error-state">Post not found</div>
      </StyledPostDetailPage>
    );
  }

  return (
    <StyledPostDetailPage>
      <Message />
      <StyledPostDetailContent>
        {/* Back button */}
        <div className="back-button" onClick={() => setLocation("/community")}>
          <span className="arrow">&larr;</span>
          <span className="text">Posts</span>
        </div>

        {/* Post content */}
        <div className="post-container">
          {/* Author header */}
          <div className="post-header">
            <ProfilePopover author={post.author} position="below">
              {post.author.profilePic ? (
                <img
                  src={imageUrl(`/profile-pics/${post.author.profilePic}`, 80)}
                  alt={post.author.name}
                  className="author-pic"
                />
              ) : (
                <div className="author-pic ghost-pic" />
              )}
              <div className="author-info">
                <span className="author-name">{post.author.name}</span>
                <span className="post-time">{formatTimeAgo(post.createdAt)}</span>
              </div>
            </ProfilePopover>
            <div className="menu-container">
              <div
                className="menu-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === 'post' ? null : 'post');
                }}
              >
                ⋯
              </div>

              {openMenuId === 'post' && (
                <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div
                    className="close-button"
                    onClick={() => setOpenMenuId(null)}
                  >
                    <img src="/images/more-menu/arrow.svg" />
                  </div>

                  {(isMyPost || user?.isAdmin) ? (
                    <>
                      <div className="delete-text">Remove post?</div>
                      <button
                        className="delete-button"
                        onClick={handleDeletePost}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="delete-text">Flag this post?</div>
                      <button
                        className="delete-button"
                        disabled={reportingId === 'post'}
                        onClick={async () => {
                          setReportingId('post');
                          try {
                            await reportContent('post', post.id);
                            sendToastMessageWithFallbackCode("Thanks for reporting! We'll review this soon.", false, 0);
                          } catch (err) {
                            sendToastMessageWithFallbackCode("Failed to send report", true, 0);
                          } finally {
                            setReportingId(null);
                            setOpenMenuId(null);
                          }
                        }}
                      >
                        {reportingId === 'post' ? (
                          <SimpleLoadingIndicator style={{ width: "13px", height: "13px", position: "static" }} />
                        ) : (
                          "Report"
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Question */}
          <h1 className="post-question">{post.question}</h1>

          {/* Cards */}
          {post.reading?.cards && post.reading.cards.length > 0 && (
            <>
              <div className="cards-section">
                {post.reading.cards.map((card, index) => {
                  const isReversed = card.name.includes('Reversed');
                  const keywords = showKeywords ? getCardKeywords(card.name, isReversed) : [];
                  return (
                    <div key={index} className="card-item">
                      <img
                        src={card.imagePath?.replace(/w=\d+|w=undefined/, 'w=200')}
                        alt={card.name}
                        className="card-image"
                        style={{ transform: isReversed ? 'rotate(180deg)' : 'none' }}
                      />
                      <div className="card-details">
                        <div className="card-name">{card.name.replace(/ \(Reversed\)| Reversed/g, ' (R)')}</div>
                        {card.position && <div className="card-position">{card.position}</div>}
                        {showKeywords && keywords.length > 0 && (
                          <div className="card-keywords">{keywords.join(' · ')}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                className="show-keywords-button"
                onClick={() => {
                  setShowKeywords(!showKeywords);
                }}
              >
                {showKeywords ? 'hide keywords' : 'show keywords'}
              </div>
            </>
          )}
        </div>

        {/* Comments section */}
        <div className="comments-section">
          {/* Add comment form */}
          <div className="add-comment">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
              placeholder="Your interpretation..."
              className="comment-input"
              rows={3}
            />
            <div className="comment-footer">
              <div className="helper-text">Try to explain the cards! It's helpful for learning.</div>
              {commentText.length >= 800 && (
                <span className="char-count">
                  {commentText.length}/{MAX_COMMENT_LENGTH}
                </span>
              )}
              <Button
                Theme={dk ? "light" : "dark"}
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submitting}
                style={{ width: "56px", height: "30px", borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
              >
                {submitting ? (
                  <SimpleLoadingIndicator style={{ width: "16px", height: "16px", position: "static", filter: dk ? "none" : "invert(1)" }} />
                ) : (
                  <img src="/images/more-menu/arrow.svg" style={{ width: "20px", height: "20px", filter: dk ? "none" : "invert(1)", transform: "rotate(-90deg)" }} />
                )}
              </Button>
            </div>
            {error && <div className="error-message">{error}</div>}
          </div>

          {post.comments.length > 0 && (
            <h2 className="comments-title">
              Interpretations ({post.comments.length})
            </h2>
          )}

          {/* Comments list */}
          <div className="comments-list">
            {post.comments.map((comment, index) => (
              <CommentCard
                key={comment.id}
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                style={{ zIndex: openMenuId === comment.id ? 10 : undefined }}
              >
                <div className="menu-container">
                  <div
                    className="menu-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === comment.id ? null : comment.id);
                    }}
                  >
                    ⋯
                  </div>

                  {openMenuId === comment.id && (
                    <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="close-button"
                        onClick={() => setOpenMenuId(null)}
                      >
                        <img src="/images/more-menu/arrow.svg" />
                      </div>

                      {(comment.authorId === user?.uuid || isMyPost || user?.isAdmin) ? (
                        <>
                          <div className="delete-text">Remove comment?</div>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="delete-text">Flag this comment?</div>
                          <button
                            className="delete-button"
                            disabled={reportingId === comment.id}
                            onClick={async () => {
                              setReportingId(comment.id);
                              try {
                                await reportContent('comment', post.id, comment.id);
                                sendToastMessageWithFallbackCode("Thanks for reporting! We'll review this soon.", false, 0);
                              } catch (err) {
                                sendToastMessageWithFallbackCode("Failed to send report", true, 0);
                              } finally {
                                setReportingId(null);
                                setOpenMenuId(null);
                              }
                            }}
                          >
                            {reportingId === comment.id ? (
                              <SimpleLoadingIndicator style={{ width: "11px", height: "11px", position: "static" }} />
                            ) : (
                              "Report"
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="comment-row">
                  <ProfilePopover author={comment.author}>
                    <div className="author-column">
                      {comment.author.profilePic ? (
                        <img
                          src={imageUrl(`/profile-pics/${comment.author.profilePic}`, 80)}
                          alt={comment.author.name}
                          className="comment-author-pic"
                        />
                      ) : (
                        <div className="comment-author-pic ghost-pic" />
                      )}
                      <span className="comment-author-name">{comment.author.name}</span>
                      {comment.author.isBookable && <span className="bookable-tag">bookable</span>}
                      <span className="comment-time">{formatTimeAgo(comment.createdAt)}</span>
                    </div>
                  </ProfilePopover>

                  <svg className="chat-triangle" width="12" height="18" viewBox="0 0 14 18">
                    <path d="M2 9L12 2V16L2 9Z" fill="#fff" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>

                  <div className="bubble-wrapper">
                    <div className="speech-bubble">
                      <div className="bubble-content">
                        <div className="comment-content">{comment.content}</div>
                        <button
                          className={`like-btn ${comment.hasLiked ? 'liked' : ''}`}
                          onClick={() => handleLikeComment(comment.id, comment.hasLiked)}
                        >
                          <svg className="heart-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {comment.hasLiked ? (
                              <path d="M2.99414 7.8476C2.99414 9.05257 3.54672 10.128 4.41138 10.8324L9.51177 15.7961C9.78352 16.0606 10.2164 16.0606 10.4882 15.7961L15.5842 10.8367C16.4519 10.1323 17.0067 9.05501 17.0067 7.8476C17.0067 5.72566 15.2931 4.00549 13.1792 4.00549C11.8548 4.00549 10.6876 4.68074 10.0004 5.70697C9.31325 4.68074 8.14597 4.00549 6.82158 4.00549C4.70774 4.00549 2.99414 5.72566 2.99414 7.8476Z" fill="#e24c4c" />
                            ) : (
                              <path d="M4.7601 10.4741L4.74429 10.4587L4.72719 10.4448C3.97439 9.83148 3.49414 8.8963 3.49414 7.8476C3.49414 6 4.98569 4.50549 6.82158 4.50549C7.97163 4.50549 8.98634 5.09117 9.58494 5.98516L10.0004 6.60564L10.4159 5.98516C11.0145 5.09117 12.0292 4.50549 13.1792 4.50549C15.0151 4.50549 16.5067 6 16.5067 7.8476C16.5067 8.89841 16.0245 9.83526 15.2691 10.4485L15.2516 10.4627L15.2355 10.4784L10.1395 15.4378C10.0618 15.5134 9.93813 15.5134 9.86049 15.4378L4.7601 10.4741Z" stroke="black" />
                            )}
                          </svg>
                          {comment.likeCount > 0 && <span className="like-count">{comment.likeCount}</span>}
                        </button>
                      </div>
                      {comment.authorId === user?.uuid ? (
                        <div className="you-tag">you</div>
                      ) : comment.authorId === post.authorId ? (
                        <div className="querent-tag">querent</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </CommentCard>
            ))}
            <div ref={commentsEndRef} />
          </div>
        </div>
      </StyledPostDetailContent>
    </StyledPostDetailPage>
  );
};

const StyledPostDetailPage = styled("div", {
  width: "100vw",
  height: vh(100),
  display: "flex",
  flexDirection: "column",
  backgroundColor: "$wash",
  color: "#000",
  overflow: "auto",

  "& .loading-container": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: vh(100),
  },

  "& .error-state": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: vh(100),
    fontSize: "14px",
    color: "#666",
  },

  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    color: "$text",
    "& .error-state": { color: "$textMuted" },
    "& .back-button": { backgroundColor: "$appBg", borderColor: "rgba(255, 255, 255, 0.6)" },
    "& .back-button img, & .close-button img": { filter: "invert(1)" },
    "& .bookable-tag": { backgroundColor: "$subtleFill", color: "$text" },
    "& .post-time, & .card-position, & .comment-time": { color: "$textMuted" },
    "& .card-image": { borderColor: "rgba(255, 255, 255, 0.25)" },
    "& .show-keywords-button": { backgroundColor: "$subtleFill", color: "$text" },
    "& .deck-name, & .char-count, & .no-comments, & .helper-text": { color: "$textMuted" },
    "& .menu-container .menu-button": { color: "$text", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } },
    "& .menu-container .menu-dropdown": { backgroundColor: "#000", borderColor: "rgba(255, 255, 255, 0.6)", color: "$text", boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)" },
    "& .menu-container .menu-dropdown .delete-button": { backgroundColor: "#e5e5e5", color: "#000", "&:hover": { backgroundColor: "#d0d0d0" } },
    "& .comment-input": {
      backgroundColor: "#000",
      color: "$text",
      borderColor: "#505050",
      "&:focus": { borderColor: "#aaa" },
      "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255, 255, 255, 0.4)", borderColor: "$appBg" },
    },
  },
});

const StyledPostDetailContent = styled("div", {
  padding: "40px",
  paddingTop: "120px",
  paddingBottom: "80px",
  maxWidth: "600px",
  margin: "0 auto",
  width: "100%",

  "@mobile": {
    padding: "20px",
    paddingTop: "130px",
    paddingBottom: "220px",
  },

  "& .back-button": {
    position: "fixed",
    top: "60px",
    left: "20px",
    backgroundColor: "$wash",
    border: "1px solid #000",
    padding: "0 14px 0 12px",
    height: "38px",
    borderRadius: ".8rem",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    zIndex: 100,
    transition: "opacity 0.2s ease",

    "@mobile": {
      top: "70px",
    },

    "&:hover": {
      opacity: 0.6,
    },

    "& .arrow": {
      marginRight: "8px",
      fontSize: "16px",
      fontWeight: "200",
    },

    "& .text": {
      fontSize: "12px",
    },
  },

  "& .post-container": {
    marginBottom: "32px",
  },

  "& .post-header": {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },

  "& .author-pic": {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  "& .ghost-pic": {
    backgroundColor: "#4a4a4a",
  },

  "& .author-info": {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },

  "& .author-name": {
    fontSize: "13px",
    fontWeight: "500",
  },

  "& .bookable-tag": {
    fontSize: "8px",
    textTransform: "uppercase",
    margin: "2px 0",
    padding: "3px 6px",
    borderRadius: "8px",
    backgroundColor: "$gray200",
    fontWeight: "300",
    color: "$gray700",
    letterSpacing: "0.04em",
  },

  "& .post-time": {
    fontSize: "10px",
    color: "$gray500",
    letterSpacing: "0.02em",
  },

  "& .menu-container": {
    position: "relative",
    marginLeft: "auto",
    alignSelf: "flex-start",
    marginTop: "-4px",

    "& .menu-button": {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      borderRadius: "10px",
      fontSize: "16px",
      color: "#333",
      transition: "background-color 0.2s ease",

      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
    },

    "& .menu-dropdown": {
      position: "absolute",
      top: "40px",
      right: "0",
      width: "140px",
      padding: "30px 14px 10px 14px",
      borderRadius: "18px",
      backgroundColor: "#fff",
      border: "1px solid #333",
      color: "#000",
      zIndex: 10,

      "& .close-button": {
        position: "absolute",
        top: "10px",
        right: "14px",
        cursor: "pointer",

        "& img": {
          width: "18px",
          height: "18px",
          transform: "rotate(-90deg)",
        },
      },

      "& .delete-text": {
        fontSize: "11px",
        marginTop: "12px",
      },

      "& .delete-button": {
        width: "100%",
        padding: "9px 16px",
        fontSize: "11px",
        textAlign: "center",
        border: "none",
        backgroundColor: "#e5e5e5",
        color: "#000",
        borderRadius: "20px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        marginTop: "12px",
        marginBottom: "6px",
        userSelect: "none",

        "&:hover": {
          backgroundColor: "#d0d0d0",
        },
      },
    },
  },

  "& .post-question": {
    fontSize: "16px",
    lineHeight: "1.3",
    fontFamily: "Arial, sans-serif",
    fontWeight: "400",
    marginBottom: "20px",
  },

  "& .cards-section": {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "16px",
  },

  "& .card-item": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "70px",
  },

  "& .card-image": {
    width: "60px",
    height: "auto",
    borderRadius: "6px",
    border: "1px solid rgba(0,0,0,0.2)",
    marginBottom: "6px",
  },

  "& .card-details": {
    textAlign: "center",
  },

  "& .card-name": {
    fontSize: "10px",
    fontWeight: "400",
    textTransform: "uppercase",
    letterSpacing: "0.02px",
  },

  "& .card-position": {
    fontSize: "10px",
    color: "$gray500",
    marginTop: "6px",
    letterSpacing: "0.02px",
    "@mobile": {
      fontSize: "11px",
    },
  },

  "& .card-keywords": {
    fontSize: "9px",
    textAlign: "center",
    fontWeight: "300",
    lineHeight: ".9rem",
    marginTop: "7px",
    textTransform: "lowercase",
    opacity: 0.9,
    letterSpacing: ".01em",
  },

  "& .show-keywords-button": {
    fontSize: "10px",
    padding: "4px 2px",
    width: "100px",
    textAlign: "center",
    backgroundColor: "$gray200",
    borderRadius: "10px",
    color: "$gray500",
    cursor: "pointer",
    marginBottom: "6px",
    "&:active": {
      opacity: 0.7,
    },
  },

  "& .deck-name": {
    fontSize: "12px",
    color: "#888",
  },

  "& .comments-section": {
    marginTop: "8px",
  },

  "& .comments-title": {
    fontSize: "24px",
    fontWeight: "100",
    fontFamily: "KeplerLightCondensed",
    marginBottom: "16px",
    marginTop: "8px",
  },

  "& .add-comment": {
    marginBottom: "24px",
  },

  "& .comment-input": {
    width: "100%",
    padding: "20px",
    lineHeight: "1.5",
    border: "1px solid $gray600",
    borderRadius: "16px",
    fontSize: "13px",
    resize: "none",
    fontFamily: "Arial, sans-serif",

    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "20px",
      backgroundColor: "rgba(0, 0, 0, .5)",
      border: "1px solid $wash",
    },
    "&::-webkit-scrollbar-track": {
      overflow: "hidden",
      marginTop: "16px",
      marginBottom: "16px",
      width: "8px",
    },

    "&:focus": {
      outline: "none",
      borderColor: "#000",
    },
  },

  "& .comment-footer": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginTop: "8px",

    "& .helper-text": {
      fontSize: "10px",
      color: "$gray800",
      letterSpacing: "0.02em",
      fontWeight: "300",
      flex: 1,
      marginTop: "-10px",
      marginLeft: "6px",
      "@mobile": {
        fontSize: "11px",
      },
    },
  },

  "& .char-count": {
    fontSize: "11px",
    color: "#888",
  },

  "& .error-message": {
    fontSize: "11px",
    color: "#f00",
    marginTop: "-6px",
    marginLeft: "6px",
  },

  "& .comments-list": {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingBottom: "80px",
  },

  "& .no-comments": {
    textAlign: "center",
    fontSize: "13px",
    color: "#888",
    padding: "40px 20px",
  },
});

const CommentCard = styled("div", {
  position: "relative",
  padding: "8px 0",
  "&:hover": {
    zIndex: 10,
  },

  "& .comment-row": {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
  },

  "& .author-column": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "58px",
    flexShrink: 0,
  },

  "& .comment-author-pic": {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  "& .comment-author-name": {
    fontSize: "9px",
    fontWeight: "500",
    marginTop: "4px",
    textAlign: "center",
    lineHeight: "1.2",
    wordBreak: "break-word",
  },

  "& .comment-time": {
    fontSize: "8px",
    color: "$gray500",
    letterSpacing: "0.02em",
    marginTop: "1px",
  },

  "& .chat-triangle": {
    marginTop: "12px",
    flexShrink: 0,
    marginRight: "-8px",
    zIndex: 1,
  },

  "& .bubble-wrapper": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    flex: 1,
    minWidth: 0,
  },

  "& .speech-bubble": {
    backgroundColor: "#fff",
    padding: "14px 16px",
    borderRadius: "18px",
    width: "100%",
    minWidth: 0,
  },

  "& .bubble-content": {
    display: "flex",
    alignItems: "center",
    gap: "0px",
  },

  "& .comment-content": {
    fontSize: "12px",
    lineHeight: "1.4",
    color: "#333",
    fontFamily: "Arial, sans-serif",
    flex: 1,
    paddingRight: "8px",
  },

  "& .querent-tag, & .you-tag": {
    fontSize: "8px",
    color: "rgb(0,0,0,0.8)",
    textTransform: "uppercase",
    borderRadius: "18px",
    padding: "3px 8px",
    textAlign: "center",
    width: "fit-content",
    marginTop: "4px",
  },

  "& .querent-tag": {
    backgroundColor: "$lightPurple",
  },

  "& .you-tag": {
    backgroundColor: "#daeea1",
  },

  "& .menu-container": {
    position: "absolute",
    top: "-12px",
    right: "-2px",

    "& .menu-button": {
      background: "none",
      border: "none",
      cursor: "pointer",
      width: "28px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "10px",
      fontSize: "14px",
      color: "#666",
      transition: "color 0.2s ease",
      userSelect: "none",

      "&:hover": {
        color: "#aaa",
        backgroundColor: "transparent",
      },
    },

    "& .menu-dropdown": {
      position: "absolute",
      top: "30px",
      right: "0",
      width: "140px",
      padding: "30px 14px 10px 14px",
      borderRadius: "18px",
      backgroundColor: "#fff",
      border: "1px solid #333",
      color: "#000",
      zIndex: 100,

      "& .close-button": {
        position: "absolute",
        top: "10px",
        right: "14px",
        cursor: "pointer",

        "& img": {
          width: "18px",
          height: "18px",
          transform: "rotate(-90deg)",
        },
      },

      "& .delete-text": {
        fontSize: "11px",
        marginTop: "12px",
      },

      "& .delete-button": {
        width: "100%",
        padding: "9px 16px",
        fontSize: "11px",
        textAlign: "center",
        border: "none",
        backgroundColor: "#e5e5e5",
        color: "#000",
        borderRadius: "20px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        marginTop: "12px",
        marginBottom: "6px",
        userSelect: "none",

        "&:hover": {
          backgroundColor: "#d0d0d0",
        },
      },
    },
  },

  "& .like-btn": {
    background: "none",
    border: "none",
    cursor: "pointer",
    height: "28px",
    paddingLeft: "6px",
    paddingRight: "8px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "background-color 0.2s ease",

    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },

    "& .heart-icon": {
      width: "18px",
      height: "18px",
    },

    "& .like-count": {
      fontSize: "11px",
      color: "#666",
    },
  },

  [`.${darkTheme.className} &`]: {
    "& .comment-time": { color: "$textMuted" },
    "& .speech-bubble": { backgroundColor: "rgb(51, 51, 51)" },
    "& .chat-triangle path": { fill: "rgb(51, 51, 51)", stroke: "rgb(51, 51, 51)" },
    "& .comment-content": { color: "$text" },
    "& .menu-container .menu-button": { color: "$textMuted", "&:hover": { color: "$text" } },
    "& .menu-container .menu-dropdown": { backgroundColor: "#000", borderColor: "rgba(255, 255, 255, 0.6)", color: "$text", boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)" },
    "& .menu-container .menu-dropdown .close-button img": { filter: "invert(1)" },
    "& .menu-container .menu-dropdown .delete-button": { backgroundColor: "#e5e5e5", color: "#000", "&:hover": { backgroundColor: "#d0d0d0" } },
    "& .like-btn:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
    "& .like-btn .heart-icon path[stroke]": { stroke: "#fff" },
    "& .like-count": { color: "$textMuted" },
  },
});

